import { useRef, type KeyboardEvent, type PointerEvent } from 'react'

import { cn } from '@/lib/utils'

/* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex -- Adjustable separators follow the WAI-ARIA window-splitter pattern. */

export function ResizeHandle({
  label,
  orientation,
  value,
  min,
  max,
  direction = 1,
  className,
  measure,
  onChange,
  onDragChange,
  onReset,
  auto = false,
}: {
  label: string
  orientation: 'horizontal' | 'vertical'
  value: number
  min: number
  max: number
  direction?: 1 | -1
  className?: string
  measure?: () => number | undefined
  onChange: (value: number, input: 'pointer' | 'keyboard') => void
  onDragChange?: (dragging: boolean) => void
  onReset?: () => void
  auto?: boolean
}) {
  const drag = useRef<{ pointerId: number; coordinate: number; value: number } | null>(null)
  const clamp = (next: number) => Math.min(max, Math.max(min, next))
  const coordinate = (event: PointerEvent) => orientation === 'vertical' ? event.clientX : event.clientY

  const start = (event: PointerEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.currentTarget.setPointerCapture(event.pointerId)
    drag.current = { pointerId: event.pointerId, coordinate: coordinate(event), value: clamp(measure?.() ?? value) }
    onDragChange?.(true)
  }

  const move = (event: PointerEvent<HTMLDivElement>) => {
    if (drag.current?.pointerId !== event.pointerId) return
    onChange(
      clamp(drag.current.value + ((coordinate(event) - drag.current.coordinate) * direction)),
      'pointer',
    )
  }

  const finish = (event: PointerEvent<HTMLDivElement>) => {
    if (drag.current?.pointerId !== event.pointerId) return
    drag.current = null
    event.currentTarget.releasePointerCapture(event.pointerId)
    onDragChange?.(false)
  }

  const keyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && onReset) {
      event.preventDefault()
      onReset()
      return
    }
    const decrement = orientation === 'vertical' ? 'ArrowLeft' : 'ArrowUp'
    const increment = orientation === 'vertical' ? 'ArrowRight' : 'ArrowDown'
    const measuredValue = clamp(measure?.() ?? value)
    let next: number | undefined
    if (event.key === decrement) next = measuredValue - (16 * direction)
    if (event.key === increment) next = measuredValue + (16 * direction)
    if (event.key === 'Home') next = min
    if (event.key === 'End') next = max
    if (next === undefined) return
    event.preventDefault()
    onChange(clamp(next), 'keyboard')
  }

  return (
    <div
      data-slot="resize-handle"
      role="separator"
      tabIndex={0}
      aria-label={label}
      aria-orientation={orientation}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={Math.round(value)}
      aria-valuetext={auto ? 'Auto' : `${Math.round(value)} pixels`}
      aria-keyshortcuts={onReset ? 'Enter' : undefined}
      title={onReset
        ? 'Drag or use arrow keys to resize. Double-click or press Enter for auto size.'
        : undefined}
      className={cn(
        'group absolute z-20 hidden touch-none border-0 bg-transparent p-0 focus-visible:bg-ring/10 focus-visible:outline-none md:flex',
        orientation === 'vertical'
          ? 'top-0 bottom-0 w-2 cursor-col-resize items-center justify-center'
          : 'right-0 left-0 h-2 cursor-row-resize items-center justify-center',
        className,
      )}
      onPointerDown={start}
      onPointerMove={move}
      onPointerUp={finish}
      onPointerCancel={finish}
      onKeyDown={keyDown}
      onDoubleClick={onReset}
    >
      <span
        aria-hidden="true"
        className={cn(
          'bg-transparent transition-colors group-hover:bg-ring/50 group-focus-visible:bg-ring/70',
          orientation === 'vertical' ? 'h-full w-px' : 'h-px w-full',
        )}
      />
    </div>
  )
}
